import { Box, Checkbox, Tooltip } from "@mui/material";
import {
  AccessTime as PendingIcon,
  CheckCircleOutline as CompletedIcon,
  MonitorWeight,
  Straighten,
  PhotoCamera,
  Description,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";

// Server returns Instant as epoch seconds (Jackson default with WRITE_DATES_AS_TIMESTAMPS=true).
export const formatDate = (value) => {
  if (value == null) return null;
  const d = typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

export const ITEM_TYPE_LABEL = {
  BODY_WEIGHT: "שקילה",
  BODY_MEASUREMENT: "מדידות",
  PROGRESS_PHOTO: "תמונה",
  FORM: "טופס",
};

export const ITEM_TYPE_ICON = {
  BODY_WEIGHT: MonitorWeight,
  BODY_MEASUREMENT: Straighten,
  PROGRESS_PHOTO: PhotoCamera,
  FORM: Description,
};

export const CheckInsFilters = ({ filter, onChange }) => (
  <>
    <FilterChip
      active={filter === "PENDING"}
      onClick={() => onChange(filter === "PENDING" ? "all" : "PENDING")}
      color={t.t2}
      label={<><PendingIcon sx={{ fontSize: 12 }} /> ממתינים להגשה</>}
    />
    <FilterChip
      active={filter === "COMPLETED"}
      onClick={() => onChange(filter === "COMPLETED" ? "all" : "COMPLETED")}
      color={t.blue}
      label={<><CompletedIcon sx={{ fontSize: 12 }} /> הוגשו ולא נבדקו</>}
    />
    <FilterChip
      active={filter === "REVIEWED"}
      onClick={() => onChange(filter === "REVIEWED" ? "all" : "REVIEWED")}
      color={t.green}
      label={<><CompletedIcon sx={{ fontSize: 12 }} /> נבדקו</>}
    />
    <FilterChip
      active={filter === "CANCELED"}
      onClick={() => onChange(filter === "CANCELED" ? "all" : "CANCELED")}
      color={t.red}
      label={<>✕ בוטלו</>}
    />
  </>
);

const cell = { padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 14px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align = "center", width }) => {
  const active = currentKey === sortKey;
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: align,
        width,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px", justifyContent: align === "center" ? "center" : "flex-start" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const StatusPill = ({ status }) => {
  if (!status) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          background: t.bg,
          color: t.t3,
          fontSize: 11,
          fontWeight: 500,
          padding: "2px 10px",
          borderRadius: "20px",
          border: `1px dashed ${t.border}`,
        }}
      >
        אין צ׳ק-אין
      </Box>
    );
  }
  if (status === "PENDING") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          background: t.amberLight,
          color: t.amberText,
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: "20px",
        }}
      >
        ⏳ ממתין
      </Box>
    );
  }
  if (status === "COMPLETED") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          background: t.blueLight,
          color: t.blueText,
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: "20px",
        }}
      >
        ✓ הוגש
      </Box>
    );
  }
  if (status === "REVIEWED") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          background: t.greenLight,
          color: t.greenText,
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: "20px",
        }}
      >
        ✓✓ נבדק
      </Box>
    );
  }
  if (status === "CANCELED") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          background: t.redLight,
          color: t.redText,
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: "20px",
        }}
      >
        ✕ בוטל
      </Box>
    );
  }
  return null;
};

const ItemChips = ({ items, onChipClick, checkInId }) => {
  if (!items || items.length === 0) {
    return <Box component="span" sx={{ color: t.t3, fontSize: 11 }}>–</Box>;
  }
  return (
    <Box sx={{ display: "inline-flex", gap: "4px" }}>
      {items.map((it, i) => {
        const Icon = ITEM_TYPE_ICON[it.type] || Description;
        const label = `${ITEM_TYPE_LABEL[it.type] || it.type}${it.required ? " (חובה)" : ""}`;
        const clickable = !!checkInId && !!onChipClick;
        return (
          <Tooltip key={i} title={label} arrow>
            <Box
              component={clickable ? "button" : "div"}
              onClick={clickable ? (e) => { e.stopPropagation(); onChipClick(it.type); } : undefined}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "6px",
                background: it.completed ? t.greenLight : t.bg,
                color: it.completed ? t.greenText : t.t3,
                border: `1px solid ${it.completed ? "rgba(45,158,126,.3)" : t.border}`,
                cursor: clickable ? "pointer" : "default",
                padding: 0,
                fontFamily: "inherit",
                "&:hover": clickable ? {
                  background: it.completed ? "rgba(45,158,126,.18)" : t.greenLight,
                  borderColor: t.green,
                  color: t.green,
                } : {},
              }}
            >
              <Icon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const CheckInsTab = ({ rows, selected, onToggleSelect, onToggleAll, sortKey, sortDir, onSortChange, onOpenProfile, onOpenActions, onOpenItem }) => {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh align="start" label="מתאמן" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="תאריך שליחה" sortKey="sentDate" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="סטטוס" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="תאריך השלמה" sortKey="completedDate" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>פריטים</Box>
          <SortableTh label="התקדמות" sortKey="itemsCompleted" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>פעולות</Box>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isChecked = selected.has(r.traineeId);
          const sent = formatDate(r.sentDate);
          const completed = formatDate(r.completedDate);
          const total = r.itemsTotal || 0;
          const done = r.itemsCompleted || 0;
          const allDone = total > 0 && done >= total;
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s",
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              <Box component="td" sx={cell}>
                {sent ? (
                  <Box component="span" sx={{ color: t.t2, fontSize: 13 }}>{sent}</Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <StatusPill status={r.status} />
              </Box>
              <Box component="td" sx={cell}>
                {completed ? (
                  <Box component="span" sx={{ color: t.t2, fontSize: 13 }}>{completed}</Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <ItemChips
                  items={r.items}
                  checkInId={r.latestCheckInId}
                  onChipClick={(itemType) => onOpenItem?.({
                    checkInId: r.latestCheckInId,
                    traineeId: r.traineeId,
                    itemType,
                  })}
                />
              </Box>
              <Box component="td" sx={cell}>
                {total > 0 ? (
                  <Box component="span" sx={{ fontWeight: 700, color: allDone ? t.green : t.t2 }}>
                    {done}/{total}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    border: `1.5px solid ${t.border}`,
                    background: t.bg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default CheckInsTab;
